import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import client from '../api/client';
import './Dashboard.css';

interface UserInfo {
  email: string;
  status: string;
  plan: string;
  quota_gb: number;
  expire_time: string;
  download_bytes: number;
  upload_bytes: number;
  connection_link: string;
}

type Platform = 'ios' | 'android' | 'windows' | 'macos';
type IOSMethod = 'new' | 'modify' | 'existing' | 'public';
type ToastKind = 'success' | 'error' | 'info';

const purchasePlanId = 'ad11043e3b4911f1944752540025c377';
const purchaseSkuId = 'ad19b9ee3b4911f1a8a152540025c377';
const regionPath = 'Settings → General → Routing → Region → Iran';

// Official Android client. Files live in /var/www/downloads on the server, outside
// the frontend build directory, so a frontend deploy does not wipe them.
const androidAppVersion = '1.0.0';
const androidApkUrl = `/downloads/douxingma-${androidAppVersion}-arm64.apk`;
const androidApkUniversalUrl = `/downloads/douxingma-${androidAppVersion}-universal.apk`;

const platforms: { key: Platform; label: string; icon: string }[] = [
  { key: 'ios', label: 'iOS（苹果手机）', icon: 'iOS' },
  { key: 'android', label: 'Android（安卓手机）', icon: 'ADR' },
  { key: 'windows', label: 'Windows', icon: 'WIN' },
  { key: 'macos', label: 'macOS', icon: 'MAC' },
];

const iosMethods: { key: IOSMethod; title: string; subtitle: string }[] = [
  { key: 'new', title: '方案一：创建新 Apple ID', subtitle: '首选，最简单' },
  { key: 'modify', title: '方案二：修改现有 Apple ID', subtitle: '有风险，谨慎使用' },
  { key: 'existing', title: '方案三：已有海外 Apple ID', subtitle: '直接下载' },
  { key: 'public', title: '方案四：使用公开账号', subtitle: '下载 Hiddify' },
];

const benefitCards = [
  ['流量永不过期', '买断制，用完为止，不设有效期，不用每月续费。'],
  ['不限速畅享', '不做廉价超售，高峰期也不降速、不限并发。'],
  ['全平台支持', 'iOS、Android、Windows、macOS 均可使用。'],
  ['轻量配置', '复制订阅链接即可导入，无需实名，无需复杂设置。'],
];

function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase();
  const platform = navigator.platform.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua) || (platform.includes('mac') && navigator.maxTouchPoints > 1)) return 'ios';
  if (ua.includes('android')) return 'android';
  if (platform.includes('win')) return 'windows';
  if (platform.includes('mac')) return 'macos';
  return 'ios';
}

function formatBytes(bytes: number) {
  if (!bytes) return '0 MB';
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function Icon({ name }: { name: string }) {
  const icons: Record<string, React.ReactNode> = {
    notice: <><path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Z" /><path d="M18 9a6 6 0 0 0-12 0c0 7-2.5 7-2.5 7h17S18 16 18 9Z" /></>,
    alert: <><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></>,
    dashboard: <><rect x="3" y="3" width="7" height="8" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="15" width="7" height="6" rx="1.5" /></>,
    star: <path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.8 1-6.1-4.4-4.3 6.1-.9L12 3Z" />,
    logout: <><path d="M10 17 15 12 10 7" /><path d="M15 12H3" /><path d="M14 3h5a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5" /></>,
    user: <><path d="M20 21a8 8 0 0 0-16 0" /><circle cx="12" cy="7" r="4" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    plan: <><path d="M12 2 3 7l9 5 9-5-9-5Z" /><path d="M3 12l9 5 9-5" /><path d="M3 17l9 5 9-5" /></>,
    bolt: <path d="M13 2 4 14h7l-1 8 10-13h-7l1-7Z" />,
    copy: <><rect x="9" y="9" width="11" height="11" rx="2" /><rect x="4" y="4" width="11" height="11" rx="2" /></>,
    book: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15Z" /></>,
    lock: <><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
    link: <><path d="M10 13a5 5 0 0 0 7.1 0l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1" /><path d="M14 11a5 5 0 0 0-7.1 0l-2 2A5 5 0 1 0 12 20.1l1.1-1.1" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    headset: <><path d="M4 13a8 8 0 0 1 16 0" /><path d="M4 13v4a2 2 0 0 0 2 2h2v-8H6a2 2 0 0 0-2 2Z" /><path d="M20 13v4a2 2 0 0 1-2 2h-2v-8h2a2 2 0 0 1 2 2Z" /></>,
    x: <><path d="m4 4 16 16" /><path d="M20 4 4 20" /></>,
  };

  const keyByName: Record<string, string> = {
    '!': 'alert',
    '🔔': 'notice',
    '✓': 'check',
    '✉': 'mail',
    '𝕏': 'x',
  };

  const icon = icons[keyByName[name] || name];
  if (!icon) return <span className="dx-icon" aria-hidden="true">{name}</span>;
  return (
    <svg className="dx-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icon}
    </svg>
  );
}

function RegionWarning({ platform }: { platform?: 'windows' | 'macos' }) {
  return (
    <div className="dx-warning-box">
      <Icon name="!" />
      <div>
        <strong>重要：Region 必须改为 Iran</strong>
        {platform && (
          <p>请先将客户端语言切换为 English。中文界面可能翻译不完整，英文路径更准确。</p>
        )}
        <p>如果没有设置为 Iran，可能会导致 Google 无法登录或连接异常。</p>
        <code>{platform ? regionPath : '设置 → 通用（General）→ 路由（Routing）→ Region → Iran'}</code>
      </div>
    </div>
  );
}

function Step({ n, title, children }: { n: number | string; title: string; children: React.ReactNode }) {
  return (
    <div className="dx-step">
      <span className="dx-step-number">{n}</span>
      <div>
        <h4>{title}</h4>
        <div className="dx-step-body">{children}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('ios');
  const [tutorialPulse, setTutorialPulse] = useState(false);
  const [tutorialHint, setTutorialHint] = useState(false);
  const [tutorialCopied, setTutorialCopied] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [toast, setToast] = useState<{ kind: ToastKind; text: string } | null>(null);
  const [showBookmarkToast, setShowBookmarkToast] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(true);
  const [selectedIOSMethod, setSelectedIOSMethod] = useState<IOSMethod>('new');
  const [showPublicPassword, setShowPublicPassword] = useState(false);
  const [showSensitiveLink, setShowSensitiveLink] = useState(false);
  const [showRawConfig, setShowRawConfig] = useState(false);
  const [showRedeemInput, setShowRedeemInput] = useState(false);
  const [upgradeCode, setUpgradeCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemSuccess, setRedeemSuccess] = useState('');
  const [redeemError, setRedeemError] = useState('');
  const navigate = useNavigate();
  const tutorialRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setSelectedPlatform(detectPlatform());
    client.get('/user/me')
      .then((res: any) => setUser(res.data))
      .catch(() => navigate('/login'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const usedBytes = (user?.download_bytes || 0) + (user?.upload_bytes || 0);
  const totalBytes = (user?.quota_gb || 0) * 1024 * 1024 * 1024;
  const usagePercent = totalBytes > 0 ? Math.min((usedBytes / totalBytes) * 100, 100) : 0;
  const remainingBytes = Math.max(totalBytes - usedBytes, 0);
  const fullLink = user?.connection_link || '';
  const inviteText = `我正在使用都行啊网络加速器，稳定访问、多平台支持，注册即送 5GB 永久流量。注册时可填写我的邀请邮箱：${user?.email || ''}`;
  const purchaseUrl = useMemo(() => {
    const sku = encodeURIComponent(JSON.stringify([{ sku_id: purchaseSkuId, count: 1 }]));
    return `https://ifdian.net/order/create?product_type=1&plan_id=${purchasePlanId}&sku=${sku}&custom_order_id=${encodeURIComponent(user?.email || '')}`;
  }, [user?.email]);

  const showToast = (kind: ToastKind, text: string) => {
    setToast({ kind, text });
    window.setTimeout(() => setToast(null), 2800);
  };

  const copyText = async (text: string, success: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('success', success);
      return true;
    } catch {
      showToast('error', '复制失败，请手动选择内容复制。');
      return false;
    }
  };

  const handleCopyLink = async () => {
    if (!fullLink) return;
    if (await copyText(fullLink, '订阅链接已复制，请打开客户端导入')) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTutorialCopyLink = async () => {
    if (!fullLink) return;
    try {
      await navigator.clipboard.writeText(fullLink);
      setTutorialCopied(true);
      window.setTimeout(() => setTutorialCopied(false), 2000);
    } catch {
      showToast('error', '复制失败，请手动选择内容复制。');
    }
  };

  const handleRedeem = async () => {
    if (!upgradeCode.trim()) {
      setRedeemError('请输入兑换码');
      return;
    }

    setRedeemLoading(true);
    setRedeemSuccess('');
    setRedeemError('');

    try {
      const res = await client.post('/user/redeem', { code: upgradeCode.trim() });
      setRedeemSuccess(res.data.message || '兑换成功');
      setUpgradeCode('');
      const userRes = await client.get('/user/me');
      setUser(userRes.data);
      window.setTimeout(() => setRedeemSuccess(''), 5000);
    } catch (error: any) {
      setRedeemError(error.response?.data?.detail || '兑换失败，请检查兑换码');
    } finally {
      setRedeemLoading(false);
    }
  };

  const goTutorial = (withHint = true) => {
    const target = detectPlatform() || 'ios';
    setSelectedPlatform(target);
    setTutorialHint(withHint);
    setTutorialPulse(true);
    tutorialRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => setTutorialPulse(false), 1500);
    if (withHint) window.setTimeout(() => setTutorialHint(false), 2600);
  };

  if (loading) return <div className="dx-loading">加载中...</div>;

  return (
    <main className="dx-page">
      {toast && <div className={`dx-toast dx-toast-${toast.kind}`}>{toast.text}</div>}
      {showBookmarkToast && <div className="dx-bookmark-toast">按 Ctrl+D（Windows）或 Cmd+D（Mac）收藏本站</div>}

      <header className="dx-header">
        <div className="dx-brand-mark" aria-hidden="true"><span /></div>
        <div className="dx-brand-copy">
          <h1>都行啊网络加速器</h1>
          <p>稳定访问 · 多平台支持 · 轻量配置</p>
        </div>
        <div className="dx-header-actions">
          <button className="dx-primary-btn" onClick={() => navigate('/admin')}><Icon name="dashboard" />管理后台</button>
          <button className="dx-secondary-btn" onClick={() => {
            setShowBookmarkToast(true);
            window.setTimeout(() => setShowBookmarkToast(false), 3000);
          }}><Icon name="star" />加入收藏</button>
          <button className="dx-secondary-btn" onClick={() => {
            localStorage.removeItem('token');
            navigate('/login');
          }}><Icon name="logout" />退出登录</button>
        </div>
      </header>

      <section className="dx-hero-grid">
        <article className="dx-card dx-summary-card">
          <h2><Icon name="user" />账户状态</h2>
          <dl className="dx-account-list">
            <div><dt>邮箱</dt><dd><a href={`mailto:${user?.email}`}>{user?.email}</a></dd></div>
            <div><dt>剩余流量</dt><dd><strong>{formatBytes(remainingBytes)}</strong></dd></div>
            <div><dt>状态</dt><dd className="dx-status-ok"><Icon name="✓" />{user?.status === 'active' ? '正常' : '未激活'}</dd></div>
            <div><dt>已用流量</dt><dd>{formatBytes(usedBytes)} / {user?.quota_gb || 0} GB</dd></div>
          </dl>
          <div className="dx-progress-row">
            <div className="dx-progress"><span style={{ width: `${Math.max(usagePercent, usagePercent > 0 ? 1 : 0)}%` }} /></div>
            <strong>{usagePercent.toFixed(2)}%</strong>
          </div>
        </article>

        <article className="dx-card dx-plan-card">
          <h2><Icon name="plan" />当前套餐</h2>
          <h3>100GB 永久流量包</h3>
          <div className="dx-pills">
            {['流量永不过期', '不限速', '全平台支持', '无需实名'].map((item) => <span key={item}>{item}</span>)}
          </div>
          <a className="dx-buy-btn" href={purchaseUrl} target="_blank" rel="noopener noreferrer">立即购买 100GB · ¥29</a>
          <p className="dx-pay-hint">支持微信 / 支付宝 <span>微信</span><span>支</span></p>
        </article>

        <article className="dx-card dx-quick-card">
          <h2><Icon name="bolt" />快速操作</h2>
          <button className="dx-primary-btn dx-full-btn" onClick={handleCopyLink}><Icon name="copy" />{copied ? '已复制' : '复制连接链接'}</button>
          <button className="dx-outline-primary dx-full-btn" onClick={() => goTutorial(true)}><Icon name="book" />查看一键导入教程</button>
          <p className="dx-private-hint"><Icon name="lock" />请勿分享你的订阅链接，它包含你的私人访问凭证。</p>
        </article>
      </section>

      <AndroidAppBanner onTutorial={() => {
        setSelectedPlatform('android');
        setTutorialPulse(true);
        tutorialRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => setTutorialPulse(false), 1500);
      }} />

      <section className="dx-card dx-upgrade">
        <div className="dx-section-head">
          <div>
            <h2>升级会员，不限速畅享</h2>
            <p>¥29 一次性买断 100GB 流量，永不过期，用完再买。没有月租，不会忘记续费，也不会因为过期断连。</p>
          </div>
          <a className="dx-buy-btn dx-buy-inline" href={purchaseUrl} target="_blank" rel="noopener noreferrer">立即购买 100GB · ¥29</a>
        </div>
        <div className="dx-benefit-grid">
          {benefitCards.map(([title, desc]) => (
            <div className="dx-benefit" key={title}>
              <strong>{title}</strong>
              <p>{desc}</p>
            </div>
          ))}
        </div>
        <p className="dx-pay-hint dx-pay-left">支持微信 / 支付宝</p>
        <div className="dx-redeem">
          <button className="dx-redeem-toggle" onClick={() => setShowRedeemInput(!showRedeemInput)}>
            {showRedeemInput ? '收起兑换码' : '已有兑换码？点击展开'}
          </button>
          {showRedeemInput && (
            <div className="dx-redeem-panel">
              <input
                value={upgradeCode}
                onChange={(event) => setUpgradeCode(event.target.value)}
                placeholder="请输入兑换码"
                disabled={redeemLoading}
              />
              <button className="dx-primary-btn" onClick={handleRedeem} disabled={redeemLoading}>
                {redeemLoading ? '兑换中...' : '兑换'}
              </button>
            </div>
          )}
          {redeemSuccess && <div className="dx-redeem-success">{redeemSuccess}</div>}
          {redeemError && <div className="dx-redeem-error">{redeemError}</div>}
        </div>
      </section>

      <section ref={tutorialRef} className={`dx-card dx-tutorial ${tutorialPulse ? 'is-pulsing' : ''}`}>
        <div className="dx-section-title-row">
          <h2><Icon name="book" />使用教程</h2>
          {tutorialHint && <span className="dx-inline-success">已为你定位到一键导入教程</span>}
        </div>
        <div className="dx-tabs" role="tablist" aria-label="平台教程">
          {platforms.map((platform) => (
            <button
              key={platform.key}
              role="tab"
              aria-selected={selectedPlatform === platform.key}
              className={selectedPlatform === platform.key ? 'is-active' : ''}
              onClick={() => setSelectedPlatform(platform.key)}
            >
              <span className={`dx-platform-icon dx-platform-icon-${platform.key}`}>{platform.icon}</span>{platform.label}
            </button>
          ))}
        </div>
        <div className="dx-tutorial-copy">
          <div className="dx-tutorial-copy-main">
            <span>订阅链接</span>
            <code>hysteria2://••••••••••••••••••••••••</code>
            <p>复制后回到 Hiddify，选择“从链接导入”或“从剪贴板导入”。</p>
          </div>
          <button className="dx-tutorial-copy-btn" onClick={handleTutorialCopyLink}>
            {tutorialCopied ? '已复制' : '复制订阅链接'}
          </button>
          {tutorialCopied && <div className="dx-tutorial-copy-success">已复制，请打开 Hiddify 导入。</div>}
        </div>
        <div className={`dx-tutorial-layout ${selectedPlatform === 'windows' || selectedPlatform === 'macos' ? 'dx-tutorial-layout-wide' : ''}`}>
          <div className="dx-tutorial-main">
            {selectedPlatform === 'ios' && (
              <IOSTutorial
                showGuide={showIOSGuide}
                setShowGuide={setShowIOSGuide}
                selectedMethod={selectedIOSMethod}
                setSelectedMethod={setSelectedIOSMethod}
                showPassword={showPublicPassword}
                setShowPassword={setShowPublicPassword}
              />
            )}
            {selectedPlatform === 'android' && <AndroidTutorial />}
            {selectedPlatform === 'windows' && <WindowsTutorial />}
            {selectedPlatform === 'macos' && <MacOSTutorial />}
          </div>
          {(selectedPlatform === 'ios' || selectedPlatform === 'android') && <DeviceMockup />}
        </div>
      </section>

      <section className="dx-card dx-config">
        <div className="dx-section-head">
          <div>
            <h2><Icon name="link" />连接配置</h2>
            <p>如果你需要扫码导入、查看原始配置，或重新复制订阅链接，可以在这里操作。</p>
          </div>
        </div>
        <div className="dx-config-row">
          <div className="dx-secret-input">
            <code>{showSensitiveLink ? fullLink : 'hysteria2://••••••••••••••••••••••••••••••'}</code>
            <button aria-label={showSensitiveLink ? '隐藏链接' : '显示链接'} onClick={() => setShowSensitiveLink(!showSensitiveLink)}>
              {showSensitiveLink ? '隐藏' : '显示'}
            </button>
          </div>
          <button className="dx-primary-btn" onClick={handleCopyLink}><Icon name="link" />复制订阅链接</button>
          <button className="dx-secondary-btn" onClick={() => setShowRawConfig(!showRawConfig)}>{showRawConfig ? '收起原始配置' : '展开原始配置'}</button>
        </div>
        <div className="dx-success-strip"><Icon name="✓" />订阅链接已生成，可直接复制使用。若导入失败，请尝试更新客户端或重新复制链接。</div>
        {fullLink && (
          <div className="dx-qr-panel">
            <div>
              <h3>扫码导入</h3>
              <p>在 Hiddify 中选择扫码或从二维码导入，即可快速添加订阅。</p>
            </div>
            <div className="dx-qr-box">
              <QRCodeCanvas value={fullLink} size={148} level="M" includeMargin />
            </div>
          </div>
        )}
        {showRawConfig && (
          <div className="dx-raw-config">
            <strong>高级用户配置，请勿分享给他人。</strong>
            <pre>{fullLink}</pre>
          </div>
        )}
      </section>

      <section className="dx-invite">
        <div className="dx-invite-icon" aria-hidden="true"><span /></div>
        <div>
          <h2>邀请好友，获得奖励</h2>
          <p>分享你的专属邀请邮箱，每成功邀请一位好友注册，你可获得 10GB 永久流量。</p>
          <p>奖励不设上限，也不会过期。</p>
        </div>
        <button className="dx-outline-primary" onClick={async () => {
          if (await copyText(inviteText, '邀请链接已复制')) {
            setInviteCopied(true);
            window.setTimeout(() => setInviteCopied(false), 2000);
          }
        }}>{inviteCopied ? '已复制' : '复制邀请链接'}</button>
      </section>

      <section className="dx-card dx-contact">
        <div>
          <h2><Icon name="headset" />联系我们</h2>
          <p>随时为你提供支持与帮助，我们通常在 24–48 小时内回复。</p>
        </div>
        <div className="dx-contact-chips">
          <a className="dx-email-chip" href="mailto:douxingma@zohomail.com"><Icon name="✉" /><span>邮箱</span>douxingma@zohomail.com</a>
          <a className="dx-x-chip" href="https://x.com/douxingma82248" target="_blank" rel="noopener noreferrer"><Icon name="𝕏" /><span>X（推特）</span>@douxingma82248</a>
        </div>
      </section>

      <footer className="dx-footer">
        <nav>
          <a href="/user-agreement">用户协议</a>
          <a href="/privacy-policy">隐私政策</a>
          <a href="/terms-of-service">服务条款</a>
        </nav>
        <p>© 2026 都行啊网络加速器 douxingma.com · 保留所有权利</p>
      </footer>
    </main>
  );
}

function IOSTutorial({
  showGuide,
  setShowGuide,
  selectedMethod,
  setSelectedMethod,
  showPassword,
  setShowPassword,
}: {
  showGuide: boolean;
  setShowGuide: (value: boolean) => void;
  selectedMethod: IOSMethod;
  setSelectedMethod: (value: IOSMethod) => void;
  showPassword: boolean;
  setShowPassword: (value: boolean) => void;
}) {
  return (
    <>
      <div className="dx-tutorial-section">
        <div className="dx-guide-head">
          <div>
            <h3>下载客户端</h3>
            <p>中国区 App Store 可能无法直接下载 Hiddify，请根据你的情况选择下载方式。</p>
          </div>
          <button className="dx-secondary-btn" onClick={() => setShowGuide(!showGuide)}>{showGuide ? '收起下载指南' : '展开下载指南'}</button>
        </div>
        {!showGuide && <p className="dx-collapsed-note">已收起 iOS 下载指南。需要更换 Apple ID 或使用公开账号时，可随时展开查看四种方案。</p>}
        {showGuide && (
          <>
            <div className="dx-ios-methods">
              {iosMethods.map((method) => (
                <button
                  key={method.key}
                  className={selectedMethod === method.key ? 'is-selected' : ''}
                  onClick={() => setSelectedMethod(method.key)}
                >
                  <strong>{method.title}</strong>
                  <span>{method.subtitle}</span>
                </button>
              ))}
            </div>
            <IOSMethodDetail method={selectedMethod} showPassword={showPassword} setShowPassword={setShowPassword} />
          </>
        )}
      </div>

      <div className="dx-tutorial-section">
        <h3>下载完成后，继续完成导入配置</h3>
        <Step n={1} title="复制订阅链接"><p>点击上方“复制订阅链接”按钮，复制后回到 Hiddify 导入。</p></Step>
        <Step n={2} title="导入配置"><p>打开 Hiddify，点击右上角 “+”，选择“从链接导入”或“从剪贴板导入”。</p></Step>
        <Step n={3} title="设置路由地区">
          <p>点击 Hiddify 左上角的“设置”图标，选择“通用”（General），进入“路由”（Routing），将 “Region” 改为 “Iran”。</p>
          <RegionWarning />
        </Step>
        <Step n={4} title="开始连接"><p>返回主页，点击 “Home”，然后点击 “Tap to Connect” 启动连接。</p></Step>
      </div>
    </>
  );
}

function IOSMethodDetail({ method, showPassword, setShowPassword }: { method: IOSMethod; showPassword: boolean; setShowPassword: (value: boolean) => void }) {
  if (method === 'new') {
    return (
      <div className="dx-method-detail">
        <h4>创建全新“香港 Apple ID”下载 Hiddify</h4>
        <div className="dx-soft-tip"><strong>购买独享账户建议</strong><p>有长期使用需求的苹果用户，如果注册有困难，为了方便与数据安全，可以考虑购买一个独享港区账户。目前圈内主流购买网站是 <a href="https://www.yuguoid.com/" target="_blank" rel="noopener noreferrer">https://www.yuguoid.com/</a>，一般价格在 15 元左右。</p></div>
        <Step n={1} title="进入 Apple ID 创建网站，创建新 ID">
          <ul><li>访问 <a href="https://appleid.apple.com/account" target="_blank" rel="noopener noreferrer">Apple ID 创建网站</a></li><li>国家和地区请选择 <strong>香港</strong></li><li>填写 <strong>从未注册过</strong> Apple ID 的邮箱</li><li>填写中国大陆手机号码</li></ul>
        </Step>
        <Step n={2} title="验证邮箱和手机号码"><p>先进行 <strong>邮箱验证</strong>，再进行 <strong>手机号码验证</strong>。如果可以进入到 <strong>登录和安全</strong> 页面，说明您的香港 Apple ID 已经注册成功。</p></Step>
        <Step n={3} title="在 App Store 中退出现有账号"><ul><li>打开 App Store，点击 <strong>右上角图标</strong></li><li>滑到页面底部</li><li>选择 <strong>退出登录</strong></li></ul></Step>
        <Step n={4} title="登录新创建的香港 Apple ID">
          <div className="dx-mini-warning"><p>注意：不要在“设置”中登录，只在 App Store 中登录。</p><p>不要点击右上角的“完成”，而是点击密码下方的“登录”。</p></div>
          <GuideImage src="/images/appstore-login-correct.png" alt="App Store 登录正确方式" />
        </Step>
        <Step n={5} title="下载 Hiddify"><p>如果 App Store 首页内容变为繁体中文，代表切换成功。在商店中搜索 <strong>Hiddify</strong> 进行下载。</p></Step>
        <div className="dx-soft-tip"><strong>温馨提醒</strong><p>新 ID 首次下载软件时，Apple 会要求补充信息。出现提示时点击“检查”，付款方式选择“无”，其它内容可填写香港地址信息。</p></div>
      </div>
    );
  }

  if (method === 'modify') {
    return (
      <div className="dx-method-detail">
        <h4>修改原 Apple ID 地区至“香港”下载 Hiddify</h4>
        <div className="dx-danger-tip">警告：如果您的 Apple ID 中存在订阅、余额或未完成的购买，请谨慎更改地区，这些内容可能会丢失。</div>
        <Step n={1} title="进入 Apple ID 登录网站"><ul><li>访问 <a href="https://appleid.apple.com/" target="_blank" rel="noopener noreferrer">Apple ID 登录网站</a></li><li>点击 <strong>登录</strong>，输入自己的 Apple ID</li><li>在 <strong>右上角下拉菜单</strong> 中选择 <strong>个人信息</strong></li></ul></Step>
        <Step n={2} title="更改国家或地区"><ul><li>在个人信息页面选择 <strong>国家或地区</strong></li><li>接着选择 <strong>更改国家或地区</strong></li><li>选择 <strong>香港</strong></li></ul><p>若提示“无法更改国家或地区”，请返回选择方案一。</p></Step>
        <Step n={3} title="填写付款方式"><ul><li><strong>付款方式</strong> 选择 <strong>无</strong></li><li>地址信息可以填写香港的任意地址</li><li>最后选择 <strong>更新</strong> 即可</li></ul><p>若必须填写付款方式无法跳过，请返回选择方案一。</p></Step>
        <Step n="4-6" title="退出并重新登录，然后下载 Hiddify"><p>步骤与方案一的步骤 3-5 相同：只在 App Store 中登录，切换成功后搜索并下载 Hiddify。</p></Step>
      </div>
    );
  }

  if (method === 'existing') {
    return (
      <div className="dx-method-detail">
        <h4>使用已有的海外 Apple ID 下载</h4>
        <Step n={1} title="下载客户端"><p>在商店中搜索 <strong>Hiddify</strong> 进行下载。</p></Step>
      </div>
    );
  }

  return (
    <div className="dx-method-detail">
      <h4>使用公开账号下载 Hiddify</h4>
      <div className="dx-danger-tip"><p>请一定严格按照教程步骤，勿擅自操作。</p><p>请勿在手机“设置”的 Apple 账户中登录公开账号，否则可能导致个人数据暴露、被抹除，甚至使设备被停用。</p></div>
      <GuideImage src="/images/warning-settings.png" alt="警告：不要在设置中登录公开账号" />
      <div className="dx-mini-warning">若提示需要“电话号码验证”，代表操作路径错误，请重新检查第二步与第三步。</div>
      <GuideImage src="/images/notice2.png" alt="出现电话验证说明操作错误" />
      <Step n={1} title="前往 App Store，退出现在的 Apple ID"><ul><li>打开 App Store，点击 <strong>右上角图标</strong></li><li>滑到页面底部</li><li>选择 <strong>退出登录</strong></li></ul><GuideImage src="/images/warning-phone.png" alt="退出当前 Apple ID" /></Step>
      <Step n={2} title="登录公开 Apple ID 后，务必点击“其他选项”"><p>如果没有“其他选项”就点击继续，跳到步骤四。</p><GuideImage src="/images/step1-logout.png" alt="选择其他选项" /></Step>
      <Step n={3} title="点击“其他选项”后，务必点击“不升级”"><p>若没出现此弹窗提示请忽略。</p><GuideImage src="/images/step2-other-options.png" alt="点击不升级" /></Step>
      <Step n={4} title="使用公共香港 Apple ID 登录">
        <p>请复制以下账号信息，并按照前面的步骤完成登录。</p>
        <GuideImage src="/images/step3-no-upgrade.png" alt="输入账号密码并登录" />
        <div className="dx-public-account">
          <p><strong>公共 Apple ID：</strong>starwarsjack@mail.com</p>
          <p><strong>密码：</strong>{showPassword ? 'Ax774411' : '••••••••'} <button onClick={() => setShowPassword(!showPassword)}>{showPassword ? '隐藏密码' : '显示密码'}</button></p>
        </div>
        <div className="dx-mini-warning"><p>若公共 Apple ID 不可用，通常是其他用户未按教程操作，将公共账号绑定手机号码并开启双重验证。可联系 <a href="mailto:douxingma@zohomail.com">douxingma@zohomail.com</a> 或 𝕏 <a href="https://x.com/douxingma82248" target="_blank" rel="noopener noreferrer">@douxingma82248</a>。</p><p>如果以上方案均有困难，可以考虑购买或租赁一个港区账户。</p></div>
      </Step>
      <Step n={5} title="下载应用"><p>如果 App Store 首页内容变为繁体中文，代表已成功切换到香港 Apple ID。在商店中搜索 <strong>Hiddify</strong> 进行下载。</p></Step>
      <Step n={6} title="退出公共账号"><p>下载完成后，在 App Store 中点击 <strong>右上角图标</strong>，滑到页面底部，点击退出登录，然后登录自己的 ID。</p><div className="dx-danger-tip">千万不要忘记登出公共 ID，长时间登录有风险。</div></Step>
    </div>
  );
}

function AndroidAppDownload() {
  return (
    <div className="dx-app-download">
      <a className="dx-download-link dx-app-download-btn" href={androidApkUrl}>下载都行啊 App（{androidAppVersion}）</a>
      <p className="dx-download-help">
        安装包由本站直接提供，无需访问 GitHub，国内网络可直接下载。
        Android 7.0 及以上可用。
      </p>
      <details className="dx-backup-download">
        <summary>提示“安装包无效 / 无法安装”？下载通用版</summary>
        <div className="dx-backup-download-body">
          <p>上方为主流手机（64 位）版本。极少数老机型需要体积更大的通用版。</p>
          <a className="dx-download-link dx-download-link-secondary" href={androidApkUniversalUrl}>下载通用版（约 60MB）</a>
        </div>
      </details>
    </div>
  );
}

function AndroidTutorial() {
  const [showHiddify, setShowHiddify] = useState(false);

  return (
    <>
      <div className="dx-tutorial-section">
        <div className="dx-recommend-tag">推荐方式 · 无需复制订阅链接</div>
        <Step n={1} title="下载并安装都行啊 App">
          <p>下载后打开安装包，若系统提示“未知来源”，按提示允许浏览器安装应用即可。</p>
          <AndroidAppDownload />
        </Step>
        <Step n={2} title="用本站账号登录"><p>打开 App，使用你在本站注册的邮箱和密码登录，无需再复制订阅链接或设置路由。</p></Step>
        <Step n={3} title="点击连接">
          <p>首次连接时，系统会弹出 VPN 授权提示，点击“确定 / 允许”即可。之后每次只需点一下连接按钮。</p>
        </Step>
      </div>

      <div className="dx-tutorial-section">
        <div className="dx-guide-head">
          <div>
            <h3>备用方案：使用 Hiddify</h3>
            <p>如果你更习惯 Hiddify，或都行啊 App 在你的设备上无法安装，可以继续使用订阅链接导入。</p>
          </div>
          <button className="dx-secondary-btn" onClick={() => setShowHiddify(!showHiddify)}>{showHiddify ? '收起' : '展开'}</button>
        </div>
        {showHiddify && (
          <>
            <Step n={1} title="下载客户端"><p>下载并安装 Hiddify Android 客户端。</p><HiddifyDownloadLinks platform="android" /></Step>
            <Step n={2} title="复制订阅链接"><p>点击上方“复制订阅链接”按钮，复制后回到 Hiddify 导入。</p></Step>
            <Step n={3} title="导入配置"><p>打开 Hiddify，选择“从剪贴板导入”或“从链接导入”。</p></Step>
            <Step n={4} title="设置路由地区"><p>进入 Hiddify 设置页面，选择“通用”（General），进入“路由”（Routing），将 “Region” 改为 “Iran”。</p><RegionWarning /></Step>
            <Step n={5} title="开始连接"><p>返回主页，选择推荐节点并点击连接。</p></Step>
          </>
        )}
      </div>
    </>
  );
}

function AndroidAppBanner({ onTutorial }: { onTutorial: () => void }) {
  const isAndroid = detectPlatform() === 'android';

  return (
    <section className="dx-card dx-app-banner">
      <div className="dx-app-banner-copy">
        <span className="dx-app-banner-tag">安卓用户推荐</span>
        <h2>都行啊 App，装完登录就能用</h2>
        <p>
          不用复制订阅链接，不用改路由地区，不用装第三方客户端。用本站账号登录后点一下即可连接，
          国内网站自动直连、境外流量走加速。
        </p>
        <div className="dx-app-banner-actions">
          <a className="dx-download-link dx-app-download-btn" href={androidApkUrl}>
            {isAndroid ? `下载安装（${androidAppVersion}）` : `下载 Android 安装包（${androidAppVersion}）`}
          </a>
          <button className="dx-secondary-btn" onClick={onTutorial}>查看安装教程</button>
        </div>
        {!isAndroid && <p className="dx-app-banner-note">当前设备不是安卓？可以在安卓手机上打开 douxingma.com 下载，其他平台请继续使用下方教程。</p>}
      </div>
    </section>
  );
}

function WindowsTutorial() {
  return (
    <div className="dx-tutorial-section">
      <Step n={1} title="下载客户端"><p>下载并安装 Windows 版本 Hiddify 客户端。</p><HiddifyDownloadLinks platform="windows" /></Step>
      <Step n={2} title="切换语言为 English"><p>Windows 客户端的中文翻译可能不完整，建议先将界面语言切换为 English，再继续设置。</p></Step>
      <Step n={3} title="复制订阅链接"><p>点击本教程顶部的“复制订阅链接”按钮，复制后回到客户端导入。</p></Step>
      <Step n={4} title="导入配置"><p>打开客户端，选择“添加订阅”或“从链接导入”。</p></Step>
      <Step n={5} title="设置路由地区"><p>在 Hiddify 中进入 Settings → General → Routing，将 Region 改为 Iran。</p><RegionWarning platform="windows" /></Step>
      <Step n={6} title="开始连接"><p>返回主页，选择推荐节点后点击连接。</p></Step>
    </div>
  );
}

function MacOSTutorial() {
  return (
    <div className="dx-tutorial-section">
      <Step n={1} title="下载客户端"><p>下载并安装 macOS 版本 Hiddify 客户端。</p><HiddifyDownloadLinks platform="macos" /></Step>
      <Step n={2} title="切换语言为 English"><p>macOS 客户端的中文翻译可能不完整，建议先将界面语言切换为 English，再继续设置。</p></Step>
      <Step n={3} title="复制订阅链接"><p>点击本教程顶部的“复制订阅链接”按钮，复制后回到客户端导入。</p></Step>
      <Step n={4} title="导入配置"><p>打开客户端，选择“从链接导入”或“添加订阅”。</p></Step>
      <Step n={5} title="设置路由地区"><p>在 Hiddify 中进入 Settings → General → Routing，将 Region 改为 Iran。</p><RegionWarning platform="macos" /></Step>
      <Step n={6} title="开始连接"><p>返回主页，选择推荐节点并点击连接。</p></Step>
    </div>
  );
}

function HiddifyDownloadLinks({ platform }: { platform: 'android' | 'windows' | 'macos' }) {
  const files = {
    android: {
      label: '下载 Hiddify Android',
      file: 'Hiddify-Android-universal.apk',
    },
    windows: {
      label: '下载 Windows 版本',
      file: 'Hiddify-Windows-Setup-x64.exe',
    },
    macos: {
      label: '下载 macOS 版本',
      file: 'Hiddify-MacOS.dmg',
    },
  };
  const item = files[platform];
  const officialUrl = `https://github.com/hiddify/hiddify-app/releases/latest/download/${item.file}`;
  const mirrorUrl = `https://ghfast.top/${officialUrl}`;

  return (
    <div className="dx-download-sources">
      <div className="dx-download-primary">
        <a className="dx-download-link" href={officialUrl} target="_blank" rel="noopener noreferrer">{item.label}</a>
      </div>
      <p className="dx-download-help">下载来自 Hiddify 官方 GitHub 仓库。国内网络环境下可能需要多试几次，或在已连接的情况下下载。</p>
      <details className="dx-backup-download">
        <summary>备用：GitHub 加速镜像</summary>
        <div className="dx-backup-download-body">
          <p>如果 GitHub 官方源无法访问，可以尝试加速镜像。</p>
          <a className="dx-download-link dx-download-link-secondary" href={mirrorUrl} target="_blank" rel="noopener noreferrer">打开加速镜像下载</a>
          <div className="dx-browser-safe-note-body">
            <p>如果 Chrome 显示 “Dangerous site” 页面，请按以下步骤继续：</p>
            <ol>
              <li>点击页面底部的 “Details”</li>
              <li>展开后找到 “Visit this unsafe site”</li>
              <li>点击后继续访问下载页面</li>
            </ol>
            <p>这是 Chrome Safe Browsing 的标准提示页面。ghfast.top 用于加速 GitHub 文件下载，部分情况下可能会被 Chrome 拦截或误报。</p>
          </div>
        </div>
      </details>
    </div>
  );
}

function DeviceMockup() {
  return (
    <aside className="dx-device">
      <div className="dx-real-phone" aria-label="Hiddify 连接成功示意图">
        <div className="dx-real-phone-crop">
          <img src="/images/hiddify-connected.png" alt="Hiddify Connected 连接成功截图" />
          <div className="dx-status-mask" aria-hidden="true" />
          <div className="dx-split-mask" aria-hidden="true" />
        </div>
      </div>
      <p>连接成功后，你会看到 “Connected” 和延迟数值，例如 “159 ms”。</p>
    </aside>
  );
}

function GuideImage({ src, alt }: { src: string; alt: string }) {
  return (
    <figure className="dx-guide-image">
      <img src={src} alt={alt} />
    </figure>
  );
}
